import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import SubtleBadge from '../../../../components/common/SubtleBadge';
import { STATISTICS_EMPTY_TABLE_ORDERS } from '../../common/lib/statisticsEmptyStateCopy';
import AdvanceTable from '../../../../components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from '../../../../components/common/advance-table/AdvanceTablePagination';
import useAdvanceTable from '../../../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../../../providers/AdvanceTableProvider';
import { useOrderContext } from '../../../../providers/OrderProvider';
import { STATISTICS_ORDERS_LIST_PAGE_SIZE } from '../../filters/lib/statisticsFilterParams';
import { socket } from '../../../../socket';
import formatCurrency from '../../../../utils/formatCurrency';
import { getOrderDeliveryStatusBadgeProps } from '../../../../utils/order/orderDeliveryStatusDisplay';
import { ORDER_APPROVAL_STATUS_LABELS } from '../../../../utils/order/orderStatus';
import { getOrderListPaymentStatusBadgeProps } from '../../../order/common/orderListTableColumns';
import OrderDetailModal from '../../../order/modal/OrderDetailModal';
import { isOverviewClosedOrder } from '../../analysis/overview/overviewMetrics';
import {
	getOrderPrimaryPhaseLabel,
	getOrderPrimaryPhaseViewPriorityBadge,
	getOrderStatisticsPrimaryPhase,
	ORDER_PRIMARY_PHASE_BADGE_TRACK,
	ORDER_PRIMARY_PHASE_META,
} from './lib/orderPrimaryPhaseConfig';

/** 与经营总览「订单列表」卡列一致：编号 / 客户 / 金额 / 状态 */
export const ORDER_TABLE_COLUMN_PRESET = {
	PIPELINE: 'pipeline',
	OVERVIEW: 'overview',
};

const phaseColorMap = Object.fromEntries(ORDER_PRIMARY_PHASE_META.map(({ phase, colorType }) => [phase, colorType]));

const PAYMENT_LABELS = {
	待收预付款: '预付款',
	待收发货款: '发货尾款',
	待收验收款: '验收尾款',
};

const DELIVERY_LABELS = {
	待发货: '未发货',
	待验收: '未验收',
};

const COMPLETE_BADGE_TEXTS = new Set(['已核验', '履约完成', '物流完成', '支付完成']);

const remapBadgeText = (badge, map) => {
	if (!badge) return null;
	const text = map[badge.text] ?? badge.text;
	return text === badge.text ? badge : { ...badge, text };
};

const toOverviewBadgeTone = (badge) => {
	if (!badge) return null;
	const bg = COMPLETE_BADGE_TEXTS.has(badge.text) ? 'success' : 'danger';
	return badge.bg === bg ? badge : { ...badge, bg };
};

const isPendingReviewRow = (row) => Boolean(row?.uiHints?.isPendingFinalCostConfirmation) || row?.primaryPhases?.operational === 'pendingReview';

const isVerifiedRow = (row) => isOverviewClosedOrder(row);

const isDualFulfillmentCompleteRow = (row) => {
	if (isVerifiedRow(row) || isPendingReviewRow(row)) return true;
	return row?.paymentStatus === 'payComplete' && row?.deliveryStatus === 'logisticsComplete';
};

export const getOverviewPaymentBadge = (row) => {
	if (row?.approvalStatus === 'reject') return null;
	if (isPendingReviewRow(row)) {
		return toOverviewBadgeTone({ bg: 'danger', icon: 'exclamation-triangle', text: '未核验' });
	}
	if (isVerifiedRow(row)) {
		return toOverviewBadgeTone({ bg: 'success', icon: 'check', text: '已核验' });
	}
	if (isDualFulfillmentCompleteRow(row)) return null;
	return toOverviewBadgeTone(
		remapBadgeText(
			getOrderListPaymentStatusBadgeProps({
				...row,
				uiHints: {
					...row?.uiHints,
					isPendingFinalCostConfirmation: false,
				},
			}),
			PAYMENT_LABELS,
		),
	);
};

export const getOverviewDeliveryBadge = (row) => {
	if (row?.approvalStatus === 'reject') return null;
	if (isDualFulfillmentCompleteRow(row)) {
		return toOverviewBadgeTone({ bg: 'success', icon: 'check', text: '履约完成' });
	}
	if (!row?.deliveryStatus) return null;
	return toOverviewBadgeTone(remapBadgeText(getOrderDeliveryStatusBadgeProps(row.deliveryStatus, row), DELIVERY_LABELS));
};

const OverviewStatusBadge = ({ badge }) => {
	if (!badge) return null;
	return (
		<SubtleBadge bg={badge.bg}>
			{badge.text}
			{badge.icon ? <FontAwesomeIcon icon={badge.icon} transform='shrink-2' className='ms-1' /> : null}
		</SubtleBadge>
	);
};

OverviewStatusBadge.propTypes = {
	badge: PropTypes.shape({
		bg: PropTypes.string,
		text: PropTypes.string,
		icon: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
	}),
};

const OrderIdCell = React.memo(({ original, onOrderClick }) => {
	const { orderId, _id } = original;
	return (
		<Link
			to='#'
			onClick={(e) => {
				e.preventDefault();
				onOrderClick(_id);
			}}
			className='text-primary text-decoration-none hover-primary'
			aria-label={`查看订单 ${orderId}`}
			title='点击查看订单详情'
		>
			{orderId}
		</Link>
	);
});

OrderIdCell.displayName = 'OrderIdCell';
OrderIdCell.propTypes = {
	original: PropTypes.object.isRequired,
	onOrderClick: PropTypes.func.isRequired,
};

const renderStatusBadge = ({ bg, text }) => (
	<Badge bg={bg} className='fs-11 me-0'>
		{text}
	</Badge>
);

const buildOverviewColumns = (onOrderClick) => [
	{
		accessorKey: 'orderId',
		header: '订单编号',
		meta: {
			headerProps: { className: 'sticky-column text-900 text-start' },
			cellProps: { className: 'fw-semibold text-start' },
		},
		cell: ({ row: { original } }) => <OrderIdCell original={original} onOrderClick={onOrderClick} />,
	},
	{
		accessorFn: (row) => row.customerInfo?.customerName || row.customerName || '',
		id: 'customerName',
		header: '客户名称',
		meta: {
			headerProps: { className: 'text-900 text-start' },
			cellProps: { className: 'text-start' },
		},
		cell: ({ row: { original } }) => original.customerInfo?.customerName || original.customerName || '未知客户',
	},
	{
		accessorKey: 'totalPrice',
		header: '金额',
		meta: { headerProps: { className: 'text-end text-900' }, cellProps: { className: 'text-end' } },
		cell: ({ cell }) => formatCurrency(cell.getValue() ?? 0),
	},
	{
		id: 'status',
		header: '状态',
		enableSorting: false,
		meta: {
			headerProps: { className: 'text-center text-900' },
			cellProps: { className: 'text-center' },
		},
		cell: ({ row: { original } }) => {
			const paymentBadge = getOverviewPaymentBadge(original);
			const deliveryBadge = getOverviewDeliveryBadge(original);
			if (!paymentBadge && !deliveryBadge) return <span className='text-500'>—</span>;
			return (
				<div className='d-flex flex-nowrap justify-content-center gap-1'>
					<OverviewStatusBadge badge={paymentBadge} />
					<OverviewStatusBadge badge={deliveryBadge} />
				</div>
			);
		},
	},
];

const buildPipelineColumns = (onOrderClick) => [
	{
		accessorKey: 'orderId',
		header: '订单编号',
		meta: {
			headerProps: { className: 'sticky-column text-900 text-start' },
			cellProps: { className: 'fw-semibold text-start' },
		},
		cell: ({ row: { original } }) => <OrderIdCell original={original} onOrderClick={onOrderClick} />,
	},
	{
		accessorFn: (row) => row.customerInfo?.customerName ?? '',
		id: 'customerName',
		header: '客户名称',
		meta: {
			headerProps: { className: 'text-900 text-start' },
			cellProps: { className: 'text-start' },
		},
		cell: ({ row: { original } }) => {
			const customerName = original.customerInfo?.customerName || '未知客户';
			return (
				<Link to={`/sales/customer/${original.customer}`} className='text-primary'>
					{customerName}
				</Link>
			);
		},
	},
	{
		accessorFn: (row) => getOrderStatisticsPrimaryPhase(row) ?? '',
		id: 'primaryPhase',
		header: '流程节点',
		cell: ({ row: { original } }) => {
			const phase = getOrderStatisticsPrimaryPhase(original);
			const colorType = phaseColorMap[phase] ?? 'secondary';
			return (
				<Badge bg={colorType} className='fs-11'>
					{getOrderPrimaryPhaseLabel(phase)}
				</Badge>
			);
		},
	},
	{
		id: 'viewPriorityStatus',
		header: '跟进状态',
		cell: ({ row: { original } }) => {
			const badge = getOrderPrimaryPhaseViewPriorityBadge(original);
			return badge ? renderStatusBadge(badge) : <span className='text-500'>-</span>;
		},
	},
	{
		accessorKey: 'approvalStatus',
		header: '审批',
		cell: ({ row: { original } }) => {
			const label = ORDER_APPROVAL_STATUS_LABELS[original.approvalStatus] ?? original.approvalStatus ?? '-';
			const bg = original.approvalStatus === 'reject' ? 'danger' : original.approvalStatus === 'inProcessOfApproval' ? 'warning' : 'success';
			return (
				<Badge bg={bg} className='fs-11'>
					{label}
				</Badge>
			);
		},
	},
	{
		accessorKey: 'totalPrice',
		header: '订单金额',
		meta: { headerProps: { className: 'text-end text-900' }, cellProps: { className: 'text-end' } },
		cell: ({ cell }) => <strong>{formatCurrency(cell.getValue() ?? 0)}</strong>,
	},
	{
		accessorKey: 'saleUserName',
		header: '销售员',
		cell: ({ cell }) => cell.getValue() || '-',
	},
	{
		accessorFn: (row) => (row.createdAt ? new Date(row.createdAt) : null),
		id: 'createdAt',
		header: '创建日期',
		cell: ({ cell }) => (cell.getValue() ? moment(cell.getValue()).format('YYYY-MM-DD') : '-'),
		sortingFn: 'datetime',
	},
];

const OrderPrimaryPhaseOrderTable = ({
	orders = [],
	isLoading = false,
	isFetching = false,
	compact = false,
	hidePagination = false,
	emptyMessage = '',
	columnPreset = ORDER_TABLE_COLUMN_PRESET.PIPELINE,
}) => {
	const { setCurrentOrderOIdState } = useOrderContext();
	const [isOpenModal, setIsOpenModal] = useState(false);

	const handleOrderClick = useCallback(
		(orderOId) => {
			setIsOpenModal(true);
			setCurrentOrderOIdState(orderOId);
			socket.emit('join order', orderOId);
		},
		[setCurrentOrderOIdState],
	);

	const tableData = useMemo(() => (Array.isArray(orders) ? orders : []), [orders]);

	const columns = useMemo(
		() =>
			columnPreset === ORDER_TABLE_COLUMN_PRESET.OVERVIEW
				? buildOverviewColumns(handleOrderClick)
				: buildPipelineColumns(handleOrderClick),
		[columnPreset, handleOrderClick],
	);

	const table = useAdvanceTable({
		data: tableData,
		columns,
		sortable: true,
		pagination: !hidePagination,
		perPage: STATISTICS_ORDERS_LIST_PAGE_SIZE,
	});

	if (isLoading) {
		return (
			<div
				className={classNames(
					'order-primary-phase-order-table',
					'order-primary-phase-order-table__loading-shell',
					compact ? 'order-primary-phase-order-table--compact' : null,
				)}
				aria-hidden='true'
			/>
		);
	}

	if (tableData.length === 0) {
		return <div className='text-center text-600 py-4 fs-10'>{emptyMessage || STATISTICS_EMPTY_TABLE_ORDERS}</div>;
	}

	return (
		<>
			<div
				role='region'
				aria-label='订单列表'
				aria-busy={isFetching}
				className={classNames(
					'order-primary-phase-order-table',
					compact ? 'order-primary-phase-order-table--compact' : null,
					isFetching ? 'order-primary-phase-order-table--fetching' : null,
				)}
			>
				<AdvanceTableProvider {...table}>
					<AdvanceTable
						headerClassName={classNames(
							'text-nowrap align-middle font-sans-serif',
							compact ? 'home-dashboard-order-table__head' : 'bg-200',
						)}
						rowClassName='btn-reveal-trigger text-nowrap align-middle'
						tableProps={{
							className: classNames(
								'fw-semibold mb-0',
								compact ? 'table-dashboard table-sm' : 'fs-10 overflow-hidden',
							),
						}}
					/>
					{hidePagination ? null : (
						<div className='order-primary-phase-order-table__pagination'>
							<AdvanceTablePagination />
						</div>
					)}
				</AdvanceTableProvider>
				{isFetching ? <span className='visually-hidden'>加载订单…</span> : null}
			</div>
			<OrderDetailModal setIsOpenModal={setIsOpenModal} isOpenModal={isOpenModal} badgeTrack={ORDER_PRIMARY_PHASE_BADGE_TRACK} />
		</>
	);
};

OrderPrimaryPhaseOrderTable.propTypes = {
	orders: PropTypes.array,
	isLoading: PropTypes.bool,
	isFetching: PropTypes.bool,
	compact: PropTypes.bool,
	hidePagination: PropTypes.bool,
	emptyMessage: PropTypes.string,
	columnPreset: PropTypes.oneOf([ORDER_TABLE_COLUMN_PRESET.PIPELINE, ORDER_TABLE_COLUMN_PRESET.OVERVIEW]),
};

export default React.memo(OrderPrimaryPhaseOrderTable);
